import { Container, Ticker } from 'pixi.js';

/**
 * A tiny, dependency-free tween layer on Pixi's shared ticker. Used for the table's "juice"
 * (card deals, flips, pops, shakes, floats). Every tween bound to a display object self-cancels
 * the moment that object is destroyed, so `SceneManager.show()` destroying a scene `{children:true}`
 * leaves nothing running — callers usually don't need to track handles, but `cancel()` is exposed
 * for the few long-lived loops (the turn-timer tick, the active-seat glow).
 */

export type Ease = (t: number) => number;
export const linear: Ease = (t) => t;
export const easeOutCubic: Ease = (t) => 1 - (1 - t) ** 3;
export const easeInCubic: Ease = (t) => t * t * t;
export const easeInOutQuad: Ease = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);
export const easeOutBack: Ease = (t) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
};

export interface TweenHandle {
  cancel(): void;
}

const NOOP: TweenHandle = { cancel() {} };

export function prefersReducedMotion(): boolean {
  if (typeof document !== 'undefined' && document.documentElement.dataset.visualFixture) return true;
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

export interface AnimateOpts {
  duration: number; // ms
  ease?: Ease;
  delay?: number; // ms
  /** Called every frame with eased progress in [0,1]. */
  onUpdate: (t: number) => void;
  onComplete?: () => void;
  /** If given and it becomes `.destroyed`, the tween self-cancels (no onComplete). */
  bind?: Container;
}

/** Core driver: runs `onUpdate(easedProgress)` each frame until `duration` elapses. */
export function animate(opts: AnimateOpts): TweenHandle {
  const ease = opts.ease ?? easeOutCubic;
  const ticker = Ticker.shared;
  let elapsed = -(opts.delay ?? 0);
  let done = false;

  const step = (): void => {
    if (done) return;
    if (opts.bind?.destroyed) {
      cancel();
      return;
    }
    elapsed += ticker.deltaMS;
    if (elapsed < 0) return; // still in the delay window
    const t = opts.duration <= 0 ? 1 : Math.min(1, elapsed / opts.duration);
    opts.onUpdate(ease(t));
    if (t >= 1) {
      done = true;
      ticker.remove(step);
      opts.onComplete?.();
    }
  };

  const cancel = (): void => {
    if (done) return;
    done = true;
    ticker.remove(step);
  };

  ticker.add(step);
  return { cancel };
}

/** Tween a set of numeric props on a plain target (captures the start values at kick-off). */
export function tweenProps(
  target: Record<string, number>,
  to: Record<string, number>,
  opts: Omit<AnimateOpts, 'onUpdate'>,
): TweenHandle {
  const keys = Object.keys(to);
  const from: Record<string, number> = {};
  for (const k of keys) from[k] = target[k];
  return animate({
    ...opts,
    onUpdate: (t) => {
      for (const k of keys) target[k] = from[k] + (to[k] - from[k]) * t;
    },
  });
}

/**
 * Pop a node in from a small scale (used for freshly dealt/flipped cards, chat bubbles, and the
 * staggered menu-page entrances — pass `delay` to cascade a row of cards/buttons).
 */
export function scaleIn(node: Container, duration = 260, from = 0.2, delay = 0): TweenHandle {
  if (node.destroyed) return NOOP;
  if (prefersReducedMotion()) {
    node.scale.set(1);
    return NOOP;
  }
  const target = 1;
  node.scale.set(from);
  return animate({
    duration,
    delay,
    ease: easeOutBack,
    bind: node,
    onUpdate: (t) => node.scale.set(from + (target - from) * t),
  });
}

/** Fade alpha to a target value. Pass `delay` to stagger (preset `node.alpha` before calling). */
export function fadeTo(node: Container, to: number, duration = 220, delay = 0): TweenHandle {
  if (node.destroyed) return NOOP;
  if (prefersReducedMotion()) {
    node.alpha = to;
    return NOOP;
  }
  const from = node.alpha;
  return animate({
    duration,
    delay,
    bind: node,
    onUpdate: (t) => {
      node.alpha = from + (to - from) * t;
    },
  });
}

/** A one-shot emphasis pulse: scale up by `peak` then settle back to 1. */
export function pulse(node: Container, peak = 1.18, duration = 320): TweenHandle {
  if (node.destroyed) return NOOP;
  if (prefersReducedMotion()) return NOOP;
  return animate({
    duration,
    ease: easeInOutQuad,
    bind: node,
    onUpdate: (t) => {
      const s = 1 + (peak - 1) * Math.sin(t * Math.PI);
      node.scale.set(s);
    },
  });
}

/** Horizontal shake that decays to zero (used for BUST / lose feedback). Restores x on finish. */
export function shake(node: Container, amplitude = 14, duration = 380): TweenHandle {
  if (node.destroyed) return NOOP;
  if (prefersReducedMotion()) return NOOP;
  const baseX = node.x;
  return animate({
    duration,
    ease: linear,
    bind: node,
    onUpdate: (t) => {
      node.x = baseX + Math.sin(t * Math.PI * 8) * amplitude * (1 - t);
    },
    onComplete: () => {
      if (!node.destroyed) node.x = baseX;
    },
  });
}

/** Slide `position` to (x,y). */
export function flyTo(node: Container, x: number, y: number, duration = 300, ease: Ease = easeOutCubic): TweenHandle {
  if (node.destroyed) return NOOP;
  if (prefersReducedMotion()) {
    node.position.set(x, y);
    return NOOP;
  }
  return tweenProps(node.position as unknown as Record<string, number>, { x, y }, { duration, ease, bind: node });
}

/**
 * A horizontal card-flip: scaleX 1→0, swap faces at the midpoint, then 0→1. The `swap` callback
 * runs once when the card is edge-on so the back→face change is hidden.
 */
export function flip(node: Container, swap: () => void, duration = 360): TweenHandle {
  if (node.destroyed) return NOOP;
  if (prefersReducedMotion()) {
    swap();
    node.scale.x = 1;
    return NOOP;
  }
  let swapped = false;
  return animate({
    duration,
    ease: easeInOutQuad,
    bind: node,
    onUpdate: (t) => {
      const sx = Math.abs(1 - 2 * t); // 1 → 0 → 1
      node.scale.x = sx;
      if (!swapped && t >= 0.5) {
        swapped = true;
        swap();
      }
    },
    onComplete: () => {
      if (!node.destroyed) node.scale.x = 1;
    },
  });
}
